/**
 * Copy / mail-app, ONLY when the board cannot send.
 *
 * These two buttons used to sit beside every draft as equal options, and the
 * send itself was an unticked checkbox underneath — so the discoverable path
 * was the manual one, and recruiters were still finishing candidate emails in
 * Gmail long after the board could send them. That also meant the board's own
 * log never recorded the email the candidate actually received, which is the
 * audit trail the Log page exists to be (Theresa, Sep 2026).
 *
 * So sending is the default, and this block appears only when it is genuinely
 * impossible: no Google connection, or no address on the Lever profile. It is a
 * real fallback for a real dead end — not a second way to do the normal thing.
 *
 * Neither button can carry an attachment. That is stated where it applies,
 * because case material silently vanishing is worse than not offering the
 * button at all.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { COLORS } from './format';

export function SendFallback({
  subject,
  body,
  candidateEmail,
  gmailConnected,
  hasAttachment = false,
}: {
  subject: string;
  body: string;
  candidateEmail: string | null;
  gmailConnected: boolean;
  hasAttachment?: boolean;
}) {
  const copy = () =>
    navigator.clipboard
      .writeText(`${subject}\n\n${body}`)
      .then(() => showToast({ status: 'success', description: 'Draft copied to clipboard.' }))
      .catch(() =>
        showToast({ status: 'error', description: 'Could not copy — select the text manually.' })
      );

  const openInMailApp = () => {
    if (!candidateEmail) return;
    // The address is encoded like every other part: a Lever profile can hold
    // anything a candidate typed, and an unescaped one silently truncates the
    // URL or drops the body.
    window.location.href = `mailto:${encodeURIComponent(candidateEmail)}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div
      className="space-y-2 rounded-lg px-3 py-2"
      style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
    >
      {/* WHY the fallback is showing, and how to get rid of it. A bare pair of
          buttons taught recruiters that copying is how this works. */}
      <p className="text-xs">
        {!gmailConnected
          ? 'The board cannot send this because no Google account is connected — connect one above and this email goes out from here, logged, with an undo. Until then:'
          : 'The board cannot send this because Lever holds no email address for this candidate — add one in Lever and this email goes out from here. Until then:'}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={copy}>
          Copy the draft
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={openInMailApp}
          disabled={!candidateEmail}
          title={candidateEmail || 'No candidate email on the Lever profile'}
        >
          Open in email app
        </Button>
      </div>
      {hasAttachment && (
        <p className="text-xs font-semibold">
          Neither of these can carry the attachment — send it separately, or connect Google and send
          from here.
        </p>
      )}
    </div>
  );
}
